import type { ReactNode } from 'react'
import { ArrowLeftIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'

// ============================================================================
// Page Header Types
// ============================================================================

export type PageHeaderSize = 'sm' | 'md' | 'lg'
export type PageHeaderVariant = 'default' | 'centered' | 'simple' | 'compact'

// ============================================================================
// Page Header Styles
// ============================================================================

const sizeStyles: Record<
  PageHeaderSize,
  { title: string; subtitle: string; gap: string; actionsGap: string }
> = {
  sm: {
    title: 'text-lg font-semibold',
    subtitle: 'text-sm',
    gap: 'gap-3',
    actionsGap: 'gap-2',
  },
  md: {
    title: 'text-xl font-semibold lg:text-2xl',
    subtitle: 'text-sm',
    gap: 'gap-4',
    actionsGap: 'gap-3',
  },
  lg: {
    title: 'text-2xl font-semibold lg:text-3xl',
    subtitle: 'text-base',
    gap: 'gap-5',
    actionsGap: 'gap-3',
  },
}

// ============================================================================
// Page Header Props
// ============================================================================

export interface PageHeaderProps {
  /**
   * Page title - can be a string or ReactNode for custom formatting
   */
  title: ReactNode
  /**
   * Optional subtitle/description text
   */
  subtitle?: ReactNode
  /**
   * Optional supporting text (shown below subtitle, useful for metadata)
   */
  supportingText?: ReactNode
  /**
   * Optional action buttons to display on the right (or bottom on mobile)
   */
  actions?: ReactNode
  /**
   * Optional breadcrumbs component to display above title
   */
  breadcrumbs?: ReactNode
  /**
   * Optional avatar or icon to display before the title
   */
  avatar?: ReactNode
  /**
   * Optional badge to display after the title (e.g., status indicator)
   */
  badge?: ReactNode
  /**
   * Optional back button configuration for navigation
   */
  backButton?: {
    label: string
    onClick: () => void
  }
  /**
   * Size variant affecting typography and spacing
   * @default 'md'
   */
  size?: PageHeaderSize
  /**
   * Layout variant
   * - default: Title left, actions right
   * - centered: Everything centered
   * - simple: No bottom margin, minimal styling
   * - compact: Reduced vertical padding
   * @default 'default'
   */
  variant?: PageHeaderVariant
  /**
   * Show a divider line below the header
   * @default false
   */
  divider?: boolean
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Children rendered below the header content (e.g., tabs, filters)
   */
  children?: ReactNode
}

// ============================================================================
// Page Header Component
// ============================================================================

/**
 * PageHeader - Untitled UI Page Header Component
 *
 * A flexible page header supporting multiple layouts, sizes, and content slots.
 * Use for consistent page titling across the application.
 *
 * @example
 * // Basic usage
 * <PageHeader
 *   title="Dashboard"
 *   subtitle="Welcome back to your workspace"
 * />
 *
 * @example
 * // With actions
 * <PageHeader
 *   title="Users"
 *   subtitle="Manage team members"
 *   actions={
 *     <>
 *       <Button color="secondary">Export</Button>
 *       <Button>Add User</Button>
 *     </>
 *   }
 * />
 *
 * @example
 * // With breadcrumbs and badge
 * <PageHeader
 *   breadcrumbs={<Breadcrumbs items={[...]} />}
 *   title="Edit User"
 *   badge={<Badge variant="success">Active</Badge>}
 *   subtitle="Update user details and permissions"
 * />
 *
 * @example
 * // With avatar (profile-style header)
 * <PageHeader
 *   avatar={<Avatar src="/user.jpg" name="John Doe" size="lg" />}
 *   title="John Doe"
 *   subtitle="john.doe@example.com"
 *   supportingText="Last active 2 hours ago"
 * />
 *
 * @example
 * // Centered variant (for landing/onboarding pages)
 * <PageHeader
 *   variant="centered"
 *   size="lg"
 *   title="Welcome to Bonnie"
 *   subtitle="Let's get your assistant set up in just a few steps"
 * />
 *
 * @example
 * // With back button
 * <PageHeader
 *   backButton={{ label: 'Back to list', onClick: () => navigate(-1) }}
 *   title="Edit Assistant"
 *   subtitle="Configure your assistant settings"
 * />
 *
 * @example
 * // With divider and tabs below
 * <PageHeader
 *   title="Settings"
 *   divider
 * >
 *   <Tabs>...</Tabs>
 * </PageHeader>
 */
export function PageHeader({
  title,
  subtitle,
  supportingText,
  actions,
  breadcrumbs,
  avatar,
  badge,
  backButton,
  size = 'md',
  variant = 'default',
  divider = false,
  className,
  children,
}: PageHeaderProps) {
  const styles = sizeStyles[size]
  const isCentered = variant === 'centered'
  const isCompact = variant === 'compact'
  const isSimple = variant === 'simple'

  // Title content with optional badge
  const titleContent = (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <h1
        className={cn(
          'truncate text-gray-900 dark:text-white',
          styles.title
        )}
      >
        {title}
      </h1>
      {badge}
    </div>
  )

  // Text content block (title, subtitle, supporting text)
  const textContent = (
    <div className={cn('min-w-0 flex-1', isCentered && 'flex flex-col items-center text-center')}>
      {titleContent}
      {subtitle && (
        <div
          className={cn(
            'mt-1 text-gray-500 dark:text-gray-400',
            styles.subtitle,
            !isCentered && 'truncate'
          )}
        >
          {subtitle}
        </div>
      )}
      {supportingText && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {supportingText}
        </p>
      )}
    </div>
  )

  // Main content (avatar + text)
  const mainContent = avatar ? (
    <div className={cn('flex min-w-0 items-center', styles.gap)}>
      <div className="shrink-0">{avatar}</div>
      {textContent}
    </div>
  ) : (
    textContent
  )

  // Actions block
  const actionsBlock = actions && (
    <div
      className={cn(
        'flex shrink-0 items-center',
        styles.actionsGap,
        isCentered && 'mt-4 justify-center'
      )}
    >
      {actions}
    </div>
  )

  return (
    <div
      className={cn(
        'min-w-0',
        !isSimple && !isCompact && 'mb-6',
        isCompact && 'mb-4',
        divider && 'border-b border-gray-200 pb-6 dark:border-gray-800',
        className
      )}
    >
      {/* Back button */}
      {backButton && (
        <div className="mb-3">
          <Button
            color="link-gray"
            size="sm"
            onPress={backButton.onClick}
            className=""
            iconLeading={ArrowLeftIcon}
          >
            {backButton.label}
          </Button>
        </div>
      )}

      {/* Breadcrumbs */}
      {breadcrumbs && <div className="mb-3">{breadcrumbs}</div>}

      {/* Header row */}
      {isCentered ? (
        // Centered layout
        <div className="flex flex-col items-center">
          {mainContent}
          {actionsBlock}
        </div>
      ) : (
        // Default/compact/simple: horizontal layout
        <div
          className={cn(
            'flex min-w-0 flex-col sm:flex-row sm:items-center sm:justify-between',
            styles.gap
          )}
        >
          {mainContent}
          {actionsBlock}
        </div>
      )}

      {/* Optional children below header (tabs, filters, etc.) */}
      {children && <div className="mt-4">{children}</div>}
    </div>
  )
}

// ============================================================================
// Convenience Components
// ============================================================================

export interface PageHeaderTitleProps {
  children: ReactNode
  className?: string
}

/**
 * Standalone title component for custom page header layouts
 */
export function PageHeaderTitle({ children, className }: PageHeaderTitleProps) {
  return (
    <h1
      className={cn(
        'text-xl font-semibold text-gray-900 dark:text-white lg:text-2xl',
        className
      )}
    >
      {children}
    </h1>
  )
}

export interface PageHeaderDescriptionProps {
  children: ReactNode
  className?: string
}

/**
 * Standalone description component for custom page header layouts
 */
export function PageHeaderDescription({ children, className }: PageHeaderDescriptionProps) {
  return (
    <p className={cn('text-sm text-gray-500 dark:text-gray-400', className)}>
      {children}
    </p>
  )
}

export interface PageHeaderActionsProps {
  children: ReactNode
  className?: string
}

/**
 * Standalone actions container for custom page header layouts
 */
export function PageHeaderActions({ children, className }: PageHeaderActionsProps) {
  return (
    <div className={cn('flex shrink-0 items-center gap-3', className)}>
      {children}
    </div>
  )
}

// ============================================================================
// Exports
// ============================================================================

export default PageHeader
